import { http } from '@/api/http';
import type {
  ConfigDefinitionDto,
  ConfigItemDto,
  ListConfigsFilters,
  UpdateSystemConfigRequestDto,
} from '@/features/admin-configs/endpoints';

/**
 * Raw transport for the GLOBAL-plane configuration store backed by
 * `/api/v1/global-configs` (PTV2-1383) — the deployment-wide counterpart of the
 * per-tenant `/api/v1/configs`. Every endpoint requires global-plane
 * `platform:MANAGE`; a caller without it gets a 403 envelope.
 *
 * The wire DTOs are identical to the tenant config store (reused from
 * `@/features/admin-configs/endpoints`); only the scope is fixed to GLOBAL —
 * the server filters the catalog/rows to GLOBAL keys, so no `scope` filter is
 * sent.
 */
export const platformConfigsEndpoints = {
  getGlobalCatalog: (options?: { signal?: AbortSignal }): Promise<ConfigDefinitionDto[]> =>
    http.get<ConfigDefinitionDto[]>('/global-configs/catalog', options),
  listGlobalConfigs: (
    filters?: Omit<ListConfigsFilters, 'scope'>,
    options?: { signal?: AbortSignal },
  ): Promise<ConfigItemDto[]> =>
    http.get<ConfigItemDto[]>('/global-configs', {
      ...options,
      query: {
        q: filters?.q,
        namespace: filters?.namespace,
        tag: filters?.tag,
        diffOnly: filters?.diffOnly,
      },
    }),
  getGlobalConfig: (key: string, options?: { signal?: AbortSignal }): Promise<ConfigItemDto> =>
    http.get<ConfigItemDto>(`/global-configs/${encodeURIComponent(key)}`, options),
  upsertGlobalConfig: (
    key: string,
    body: UpdateSystemConfigRequestDto,
    options?: { signal?: AbortSignal },
  ): Promise<ConfigItemDto> =>
    http.put<ConfigItemDto>(`/global-configs/${encodeURIComponent(key)}`, body, options),
  resetGlobalConfig: (key: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/global-configs/${encodeURIComponent(key)}`, options),
};
